import { useMemo, useState } from 'react';
import {
    isKeyboardLayout,
    keyboardSwitcher,
} from '@alfa-bank/site-helpers-common/browser/keyboard-switcher';
import findKey from 'lodash/findKey';
import useAsync from 'react-use/lib/useAsync';
import sortBy from 'lodash/sortBy';
import { httpClient } from '@alfa-bank/site-http-client';

import type { TAliases, TGeoGroups } from '../../models';
import type { ICitiesBySearchResponse, ICity, ISearchParams } from '../model/http';
import { citiesEndPoint } from '../config';

const searchCities = ({ cityByAlias, geoGroups, searchWord }: ISearchParams) =>
    httpClient
        .get<ICitiesBySearchResponse>(citiesEndPoint, {
            limit: 30,
            'geoGroupCodes.includeAny': geoGroups.join(','),
            ...(searchWord && { 'cityNameRus.contains': searchWord }),
            ...(cityByAlias && { 'fiasId.eq': cityByAlias }),
        })
        .then(({ data }) => data.data);

export function useCitiesBySearch({
    aliases,
    geoGroups,
}: {
    aliases: TAliases;
    geoGroups: TGeoGroups;
}) {
    const [searchWord, setSearchWord] = useState('');
    const normalizedSearchValue = useMemo(
        () => (isKeyboardLayout(searchWord, 'ru') ? searchWord : keyboardSwitcher(searchWord)),
        [searchWord]
    );
    const cityByAlias = useMemo(
        () =>
            normalizedSearchValue &&
            findKey(aliases, words =>
                words.find(word => word.toLowerCase().startsWith(normalizedSearchValue))
            ),
        [normalizedSearchValue, aliases]
    );

    const response = useAsync(async () => {
        if (normalizedSearchValue.length === 0) return null;

        const requests = [
            searchCities({ geoGroups, searchWord: normalizedSearchValue, cityByAlias: null }),
        ];
        if (cityByAlias) {
            requests.push(searchCities({ geoGroups, cityByAlias, searchWord: '' }));
        }

        const [cities, citiesByAlias = []] = await Promise.all(requests);

        const sortedCities: ICity[] = sortBy(
            // вынужденная мера зла, бекенд не может реализовать поиск через startWith, поэтому стоит дополнительная фильтрация на фронте
            cities.filter(city =>
                city.cityNameRus.toLowerCase().startsWith(normalizedSearchValue.toLowerCase())
            ),
            'cityNameRus'
        );
        /** города с одинаковыми названиями помечаем флагом hasNamesake */
        for (let i = 0; i < sortedCities.length - 1; i += 1) {
            const city = sortedCities[i];
            const nextCity = sortedCities[i + 1];
            // istanbul ignore else
            if (city.cityNameRus === nextCity.cityNameRus) {
                city.hasNamesake = true;
                nextCity.hasNamesake = true;
            }
        }

        return (citiesByAlias as ICity[]).concat(sortedCities);
    }, [normalizedSearchValue, geoGroups]);

    return {
        searchWord,
        setSearchWord,
        cities: response.value ?? null,
    };
}
